import i18n from "i18next";
import { initReactI18next } from "react-i18next";
import sv from "./locales/sv.json";
import en from "./locales/en.json";
import fr from "./locales/fr.json";
import de from "./locales/de.json";
import ja from "./locales/ja.json";

export const LOCALE_STORAGE_KEY = "andrum.locale";
export const supportedLocales = ["sv", "en", "fr", "de", "ja"] as const;
export type AppLocale = (typeof supportedLocales)[number];

export function isAppLocale(value: string | null | undefined): value is AppLocale {
  return (
    value === "sv" ||
    value === "en" ||
    value === "fr" ||
    value === "de" ||
    value === "ja"
  );
}

export function detectLocale(): AppLocale {
  if (typeof window === "undefined") return "sv";
  try {
    const stored = localStorage.getItem(LOCALE_STORAGE_KEY);
    if (isAppLocale(stored)) return stored;
  } catch {
    // ignore
  }
  const nav = (navigator.language || "sv").toLowerCase();
  if (nav.startsWith("en")) return "en";
  if (nav.startsWith("fr")) return "fr";
  if (nav.startsWith("de")) return "de";
  if (nav.startsWith("ja")) return "ja";
  return "sv";
}

export function getLocale(): AppLocale {
  const lng = i18n.language?.split("-")[0];
  return isAppLocale(lng) ? lng : "sv";
}

export function setLocale(locale: AppLocale): void {
  try {
    localStorage.setItem(LOCALE_STORAGE_KEY, locale);
  } catch {
    // ignore
  }
  void i18n.changeLanguage(locale);
  if (typeof document !== "undefined") {
    document.documentElement.lang = locale;
  }
}

let initPromise: Promise<typeof i18n> | null = null;

export function initI18n(): Promise<typeof i18n> {
  if (i18n.isInitialized) return Promise.resolve(i18n);
  if (initPromise) return initPromise;

  const lng = detectLocale();

  initPromise = i18n
    .use(initReactI18next)
    .init({
      resources: {
        sv: { translation: sv },
        en: { translation: en },
        fr: { translation: fr },
        de: { translation: de },
        ja: { translation: ja },
      },
      lng,
      fallbackLng: "en",
      supportedLngs: [...supportedLocales],
      interpolation: {
        escapeValue: false,
      },
      returnNull: false,
      react: {
        useSuspense: false,
      },
    })
    .then(() => {
      if (typeof document !== "undefined") {
        document.documentElement.lang = i18n.language.split("-")[0] ?? "sv";
      }
      return i18n;
    });

  return initPromise;
}

/** Non-React access after init (API helpers, stores). */
export function t(
  key: string,
  options?: Record<string, string | number | boolean>
): string {
  if (options) return i18n.t(key, options);
  return i18n.t(key);
}

export { i18n };
export default i18n;
